<template>
  <div class="report-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>数据报表</span>
        </div>
      </template>

      <!-- 时间筛选 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="客户">
          <el-input
            v-model="selectedCustomerName"
            placeholder="请选择客户"
            readonly
            style="width: 200px"
            @click="openCustomerDialog"
          >
            <template #suffix>
              <el-icon class="customer-select-icon" @click="openCustomerDialog">
                <Search />
              </el-icon>
            </template>
          </el-input>
          <el-button
            v-if="queryParams.customerId"
            type="danger"
            :icon="Close"
            circle
            size="small"
            style="margin-left: 8px"
            @click="clearCustomer"
          />
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 300px"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
        <el-form-item>
          <el-button-group>
            <el-button :type="activeQuickDate === 'today' ? 'primary' : ''" @click="handleQuickDate('today')">今天</el-button>
            <el-button :type="activeQuickDate === 'week' ? 'primary' : ''" @click="handleQuickDate('week')">本周</el-button>
            <el-button :type="activeQuickDate === 'month' ? 'primary' : ''" @click="handleQuickDate('month')">本月</el-button>
            <el-button :type="activeQuickDate === 'quarter' ? 'primary' : ''" @click="handleQuickDate('quarter')">近三个月</el-button>
            <el-button :type="activeQuickDate === 'halfYear' ? 'primary' : ''" @click="handleQuickDate('halfYear')">近半年</el-button>
            <el-button :type="activeQuickDate === 'year' ? 'primary' : ''" @click="handleQuickDate('year')">近一年</el-button>
          </el-button-group>
        </el-form-item>
      </el-form>

      <!-- 统计卡片 -->
      <el-row :gutter="20" v-if="statistics">
        <el-col :span="6">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-item">
              <div class="stat-label">客户总数</div>
              <div class="stat-value">{{ formatNumber(statistics.customerCount || 0) }}</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-item">
              <div class="stat-label">合同总数</div>
              <div class="stat-value">{{ formatNumber(statistics.contractCount || 0) }}</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-item">
              <div class="stat-label">成交金额</div>
              <div class="stat-value" style="color: #67c23a">¥{{ formatMoney(statistics.totalDealAmount || 0) }}</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-item">
              <div class="stat-label">跟进次数</div>
              <div class="stat-value">{{ formatNumber(statistics.followCount || 0) }}</div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 图表区域 -->
      <el-row :gutter="20" style="margin-top: 20px">
        <!-- 客户趋势图 -->
        <el-col :span="12">
          <el-card shadow="hover" v-loading="loading">
            <template #header>
              <span>客户趋势</span>
            </template>
            <v-chart
              v-if="customerTrendOption"
              :option="customerTrendOption"
              style="height: 300px"
              autoresize
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
        
        <!-- 合同趋势图 -->
        <el-col :span="12">
          <el-card shadow="hover" v-loading="loading">
            <template #header>
              <span>合同趋势</span>
            </template>
            <v-chart
              v-if="contractTrendOption"
              :option="contractTrendOption"
              style="height: 300px"
              autoresize
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" style="margin-top: 20px">
        <!-- 跟进类型分布 -->
        <el-col :span="12">
          <el-card shadow="hover" v-loading="loading">
            <template #header>
              <span>跟进类型分布</span>
            </template>
            <v-chart
              v-if="followTypeOption"
              :option="followTypeOption"
              style="height: 300px"
              autoresize
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
        
        <!-- 成交金额趋势（按日期） -->
        <el-col :span="12">
          <el-card shadow="hover" v-loading="loading">
            <template #header>
              <span>成交金额趋势</span>
            </template>
            <v-chart
              v-if="dealAmountOption"
              :option="dealAmountOption"
              style="height: 300px"
              autoresize
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" style="margin-top: 20px">
        <!-- 成交金额按客户统计 -->
        <el-col :span="24">
          <el-card shadow="hover" v-loading="loading">
            <template #header>
              <span>成交金额统计(前10名)</span>
            </template>
            <v-chart
              v-if="customerDealAmountOption"
              :option="customerDealAmountOption"
              style="height: 400px"
              autoresize
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
      </el-row>
    </el-card>

    <!-- 客户选择对话框 -->
    <el-dialog
      v-model="customerDialogVisible"
      title="选择客户"
      width="800px"
      :close-on-click-modal="false"
    >
      <div class="customer-dialog-content">
        <!-- 搜索表单 -->
        <el-form :inline="true" :model="customerSearchForm" class="customer-search-form">
          <el-form-item label="客户姓名">
            <el-input
              v-model="customerSearchForm.customerName"
              placeholder="请输入客户姓名"
              clearable
              style="width: 200px"
              @keyup.enter="handleCustomerSearch"
            />
          </el-form-item>
          <el-form-item label="手机号">
            <el-input
              v-model="customerSearchForm.contactPhone"
              placeholder="请输入手机号"
              clearable
              style="width: 200px"
              @keyup.enter="handleCustomerSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :icon="Search" @click="handleCustomerSearch">搜索</el-button>
            <el-button :icon="Refresh" @click="handleCustomerReset">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 客户列表 -->
        <el-table
          v-loading="customerTableLoading"
          :data="customerTableData"
          highlight-current-row
          @current-change="handleCustomerRowClick"
          style="width: 100%"
        >
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="customerName" label="客户姓名" min-width="120" />
          <el-table-column prop="contactPhone" label="手机号" width="130" />
          <el-table-column prop="companyName" label="公司名称" min-width="150" show-overflow-tooltip />
        </el-table>

        <!-- 分页 -->
        <div class="customer-pagination">
          <el-pagination
            v-model:current-page="customerPagination.page"
            v-model:page-size="customerPagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="customerPagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleCustomerPageSizeChange"
            @current-change="handleCustomerPageChange"
          />
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="customerDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmSelectCustomer">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Close, Refresh } from '@element-plus/icons-vue'
import { getStatistics, getCustomerList } from '../api/salesman'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'

use([CanvasRenderer, BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent])

const loading = ref(false)
const statistics = ref(null)
const queryParams = reactive({
  startDate: '',
  endDate: '',
  customerId: null
})

// 日期范围（用于日期选择器）
const dateRange = ref([])

// 当前选中的快捷时间类型
const activeQuickDate = ref('')

// 选中的客户名称（用于回显）
const selectedCustomerName = ref('')

// 客户选择对话框相关
const customerDialogVisible = ref(false)
const customerTableLoading = ref(false)
const customerTableData = ref([])
const currentSelectedCustomer = ref(null)
const customerSearchForm = reactive({
  customerName: '',
  contactPhone: ''
})
const customerPagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0
})

// 打开客户选择对话框
const openCustomerDialog = () => {
  customerDialogVisible.value = true
  // 重置搜索条件
  customerSearchForm.customerName = ''
  customerSearchForm.contactPhone = ''
  customerPagination.page = 1
  // 加载客户列表
  loadCustomerTableData()
}

// 加载客户列表（用于对话框）
const loadCustomerTableData = async () => {
  customerTableLoading.value = true
  try {
    const params = {
      page: customerPagination.page,
      pageSize: customerPagination.pageSize,
      customerName: customerSearchForm.customerName || undefined,
      contactPhone: customerSearchForm.contactPhone || undefined
    }
    const res = await getCustomerList(params)
    if (res.code === '000000' && res.data) {
      customerTableData.value = res.data.list || []
      customerPagination.total = res.data.total || 0
    } else {
      ElMessage.error(res.msg || '获取客户列表失败')
      customerTableData.value = []
      customerPagination.total = 0
    }
  } catch (error) {
    console.error('获取客户列表失败:', error)
    ElMessage.error('获取客户列表失败')
    customerTableData.value = []
    customerPagination.total = 0
  } finally {
    customerTableLoading.value = false
  }
}

// 客户搜索
const handleCustomerSearch = () => {
  customerPagination.page = 1
  loadCustomerTableData()
}

// 客户搜索重置
const handleCustomerReset = () => {
  customerSearchForm.customerName = ''
  customerSearchForm.contactPhone = ''
  customerPagination.page = 1
  loadCustomerTableData()
}

// 客户列表行点击
const handleCustomerRowClick = (row) => {
  currentSelectedCustomer.value = row
}

// 客户分页大小改变
const handleCustomerPageSizeChange = () => {
  customerPagination.page = 1
  loadCustomerTableData()
}

// 客户分页改变
const handleCustomerPageChange = () => {
  loadCustomerTableData()
}

// 确认选择客户
const confirmSelectCustomer = () => {
  if (!currentSelectedCustomer.value) {
    ElMessage.warning('请先选择一个客户')
    return
  }
  // 设置选中的客户
  queryParams.customerId = currentSelectedCustomer.value.id
  selectedCustomerName.value = currentSelectedCustomer.value.customerName || ''
  // 关闭对话框
  customerDialogVisible.value = false
  // 自动执行搜索统计
  loadStatistics()
}

// 清除客户选择
const clearCustomer = () => {
  queryParams.customerId = null
  selectedCustomerName.value = ''
  currentSelectedCustomer.value = null
  // 自动执行搜索统计
  loadStatistics()
}

const loadStatistics = async () => {
  loading.value = true
  try {
    const params = {
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    }
    // 如果选择了客户，则添加客户ID参数
    if (queryParams.customerId) {
      params.customerId = queryParams.customerId
    }
    const res = await getStatistics(params)
    if (res.code === '000000') {
      statistics.value = res.data || {}
    }
  } catch (error) {
    console.error('获取统计数据失败:', error)
    ElMessage.error('获取统计数据失败')
  } finally {
    loading.value = false
  }
}

// 格式化数字
const formatNumber = (num) => {
  if (!num) return '0'
  return Number(num).toLocaleString('zh-CN')
}

// 格式化金额
const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 客户趋势图配置
const customerTrendOption = computed(() => {
  if (!statistics.value || !statistics.value.customerTrend) return null
  const trend = statistics.value.customerTrend
  // 确保 values 是数字数组
  const values = (trend.values || []).map(v => Number(v) || 0)
  // 格式化日期显示（只显示月-日）
  const dates = (trend.dates || []).map(d => {
    if (d && d.length >= 10) {
      return d.substring(5) // 从 "2025-12-25" 提取 "12-25"
    }
    return d
  })
  return {
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      name: '新增客户',
      type: 'line',
      data: values,
      smooth: true,
      itemStyle: { color: '#409eff' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
            { offset: 1, color: 'rgba(64, 158, 255, 0.1)' }
          ]
        }
      }
    }]
  }
})

// 合同趋势图配置
const contractTrendOption = computed(() => {
  if (!statistics.value || !statistics.value.contractTrend) return null
  const trend = statistics.value.contractTrend
  // 确保 values 是数字数组
  const values = (trend.values || []).map(v => Number(v) || 0)
  // 格式化日期显示（只显示月-日）
  const dates = (trend.dates || []).map(d => {
    if (d && d.length >= 10) {
      return d.substring(5) // 从 "2025-12-25" 提取 "12-25"
    }
    return d
  })
  return {
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      name: '新增合同',
      type: 'bar',
      data: values,
      itemStyle: { 
        color: '#67c23a',
        borderRadius: [4, 4, 0, 0]
      }
    }]
  }
})

// 跟进类型分布图配置
const followTypeOption = computed(() => {
  if (!statistics.value || !statistics.value.followTypeDistribution || statistics.value.followTypeDistribution.length === 0) return null
  const data = statistics.value.followTypeDistribution
  // 定义颜色数组
  const colors = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#606266']
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      top: 'middle'
    },
    series: [{
      name: '跟进类型',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['60%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: true,
        formatter: '{b}\n{d}%'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 14,
          fontWeight: 'bold'
        },
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      },
      data: data.map((item, index) => ({
        value: Number(item.count) || 0,
        name: item.type || '未知',
        itemStyle: {
          color: colors[index % colors.length]
        }
      }))
    }]
  }
})

// 成交金额趋势图配置（按日期）
const dealAmountOption = computed(() => {
  if (!statistics.value || !statistics.value.dealAmountTrend) return null
  const trend = statistics.value.dealAmountTrend
  // 将 BigDecimal 转换为数字
  const values = (trend.values || []).map(v => {
    if (typeof v === 'object' && v !== null) {
      // 如果是 BigDecimal 对象，转换为数字
      return Number(v.toString())
    }
    return Number(v) || 0
  })
  // 格式化日期显示（只显示月-日）
  const dates = (trend.dates || []).map(d => {
    if (d && d.length >= 10) {
      return d.substring(5) // 从 "2025-12-25" 提取 "12-25"
    }
    return d
  })
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const value = params[0].value
        return `${params[0].name}<br/>${params[0].seriesName}: ¥${formatMoney(value)}`
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => {
          if (value >= 10000) {
            return (value / 10000).toFixed(1) + '万'
          }
          return value
        }
      }
    },
    series: [{
      name: '成交金额',
      type: 'line',
      data: values,
      smooth: true,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(245, 108, 108, 0.3)' },
            { offset: 1, color: 'rgba(245, 108, 108, 0.1)' }
          ]
        }
      },
      itemStyle: { color: '#f56c6c' }
    }]
  }
})

// 成交金额按客户统计图配置
const customerDealAmountOption = computed(() => {
  if (!statistics.value || !statistics.value.customerDealAmountList || statistics.value.customerDealAmountList.length === 0) {
    return null
  }
  
  // 后端已经只返回前10名客户，这里直接使用
  const data = statistics.value.customerDealAmountList
  
  // 提取客户名称和成交金额
  const customerNames = data.map(item => {
    const name = item.customerName || '未知客户'
    // 如果名称太长，截断
    return name.length > 10 ? name.substring(0, 10) + '...' : name
  })
  const dealAmounts = data.map(item => {
    if (typeof item.dealAmount === 'object' && item.dealAmount !== null) {
      return Number(item.dealAmount.toString())
    }
    return Number(item.dealAmount) || 0
  })
  
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params) => {
        const param = params[0]
        const index = param.dataIndex
        const customer = data[index]
        return `${customer.customerName}<br/>成交金额: ¥${formatMoney(param.value)}<br/>成交次数: ${customer.dealCount || 0}次`
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: customerNames,
      axisLabel: {
        rotate: 45,
        interval: 0
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => {
          if (value >= 10000) {
            return (value / 10000).toFixed(1) + '万'
          }
          return value
        }
      }
    },
    series: [{
      name: '成交金额',
      type: 'bar',
      data: dealAmounts,
      itemStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: '#67c23a' },
            { offset: 1, color: '#85ce61' }
          ]
        },
        borderRadius: [4, 4, 0, 0]
      },
      label: {
        show: true,
        position: 'top',
        formatter: (params) => {
          const value = params.value
          if (value >= 10000) {
            return (value / 10000).toFixed(1) + '万'
          }
          return value.toLocaleString('zh-CN')
        }
      }
    }]
  }
})

const handleSearch = () => {
  // 处理日期范围
  if (dateRange.value && dateRange.value.length === 2) {
    queryParams.startDate = dateRange.value[0]
    queryParams.endDate = dateRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  // 点击查询时，不清除快捷筛选的选中状态，直接使用当前的时间参数查询
  loadStatistics()
}

// 当用户手动修改日期选择器时，清除快捷筛选的选中状态
const handleDateChange = () => {
  activeQuickDate.value = ''
}


const handleReset = () => {
  // 重置为今天
  const today = new Date()
  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  const todayStr = formatDate(today)
  dateRange.value = [todayStr, todayStr]
  queryParams.startDate = todayStr
  queryParams.endDate = todayStr
  // 重置客户选择
  queryParams.customerId = null
  selectedCustomerName.value = ''
  currentSelectedCustomer.value = null
  activeQuickDate.value = 'today'
  loadStatistics()
}

// 快捷时间筛选
const handleQuickDate = (type) => {
  // 设置当前选中的快捷时间类型
  activeQuickDate.value = type
  
  const today = new Date()
  const endDate = new Date(today)
  let startDate = new Date(today)
  
  switch (type) {
    case 'today': {
      // 今天
      startDate = new Date(today)
      break
    }
    case 'week': {
      // 本周（周一至今天）
      const dayOfWeek = today.getDay()
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // 周一是0，周日是6
      startDate.setDate(today.getDate() - diff)
      break
    }
    case 'month': {
      // 本月（1号至今天）
      startDate = new Date(today.getFullYear(), today.getMonth(), 1)
      break
    }
    case 'quarter': {
      // 近三个月
      startDate.setMonth(today.getMonth() - 3)
      break
    }
    case 'halfYear': {
      // 近半年
      startDate.setMonth(today.getMonth() - 6)
      break
    }
    case 'year': {
      // 近一年
      startDate.setFullYear(today.getFullYear() - 1)
      break
    }
  }
  
  // 格式化日期
  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  dateRange.value = [formatDate(startDate), formatDate(endDate)]
  queryParams.startDate = dateRange.value[0]
  queryParams.endDate = dateRange.value[1]
  loadStatistics()
}

onMounted(() => {
  // 默认选择今天并执行搜索
  handleQuickDate('today')
})
</script>

<style scoped>
.report-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.search-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.search-form :deep(.el-button-group) {
  margin-left: 10px;
}

.stat-card {
  text-align: center;
}

.stat-item {
  padding: 20px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
}

/* 客户选择相关样式 */
.customer-select-icon {
  cursor: pointer;
  color: #409eff;
  transition: color 0.3s;
}

.customer-select-icon:hover {
  color: #66b1ff;
}

.customer-dialog-content {
  padding: 10px 0;
}

.customer-search-form {
  margin-bottom: 20px;
}

.customer-search-form :deep(.el-form-item) {
  margin-bottom: 10px;
}

.customer-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

